import type * as AddPersonalNutritionItems from '@ori-events/add-personal-nutrition-items';
import * as AddPersonalNutritionItemsFailed from '@ori-events/add-personal-nutrition-items-failed';
import type * as BasketUpdated from '@ori-events/basket-updated';
import type { PositionData } from '@ori/analytics-data';
import { HttpMethod } from '@ori/checkout-utils';
import { type GlobalShoppingPersonalNutritionComponentsDto } from '@ori/global-api-client-react';

import { addPersonalNutritionItems } from '../api';
import { mainLogger } from '../logger';
import { RequestUrl } from '../types';
import type { HandlerSettings } from '../types';
import { convertToCamelCase, getHttpRequestSettings } from '../utils';

type Data = PositionData & AddPersonalNutritionItems.Data;

export async function addPersonalNutritionItemsHandler(
  { detail: { items } }: CustomEvent<Data>,
  { client, sessionId, tenant }: HandlerSettings,
) {
  const globalShoppingPersonalNutritionComponentsDto: GlobalShoppingPersonalNutritionComponentsDto = {
    collection: items,
  };

  try {
    await addPersonalNutritionItems({
      client,
      globalShoppingPersonalNutritionComponentsDto,
      sessionId,
      tenant,
    });
  } catch (error: unknown) {
    mainLogger.error(error as Error, {
      message: 'Error while adding personal nutrition items in addPersonalNutritionItems event',
    });

    const errorEvent = new CustomEvent<AddPersonalNutritionItemsFailed.Data>(AddPersonalNutritionItemsFailed.Name, {
      detail: {
        isSuccess: false,
        localizedMessage: String(error),
        message: String(error),
      },
    });
    window.dispatchEvent(errorEvent);

    return false;
  }

  // STEP 2 - Update shopping context via Online endpoint
  const response = await fetch(RequestUrl.GetShoppingContext, getHttpRequestSettings(undefined, HttpMethod.Post));
  const rawData = convertToCamelCase<BasketUpdated.Data>((await response.json()) as object);

  if (AddPersonalNutritionItemsFailed.isAddPersonalNutritionItemsErrorResponse(rawData)) {
    const errorEvent = new CustomEvent<AddPersonalNutritionItemsFailed.Data>(AddPersonalNutritionItemsFailed.Name, {
      detail: rawData,
    });
    window.dispatchEvent(errorEvent);

    return false;
  }

  const data = {
    ...rawData,
    isPersonalNutritionAddedAsLast: true,
  };

  return data;
}
